/**
 * The insights of a Live Shift tab (machine or line), most important first:
 * a coloured dot per finding (red missed, amber close, blue context, green
 * met) and one sentence with its values. The rules are in liveShiftInsights.ts.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { cx } from "@digitalisation/enlixy-comp";
import type { Kpi } from "../../services/KpiTargetService";
import { formatHm, formatHms, localTime, type Insight, type InsightSeverity } from "./liveShiftInsights";

const DOT: Record<InsightSeverity, string> = {
  bad: "bg-red-500",
  warn: "bg-amber-400",
  info: "bg-blue-400",
  ok: "bg-emerald-500",
};

const num = (v: number, decimals = 0) =>
  v.toLocaleString(undefined, { maximumFractionDigits: decimals });

const kpiLabel = (t: TFunction, kpi: Kpi) =>
  kpi === "minMTBF"
    ? "MTBF"
    : kpi === "rejectRatio"
      ? t("liveTargets.rejectRatio")
      : kpi === "maxDowntime"
        ? t("downtime", "Downtime")
        : t(kpi);

const kpiValue = (kpi: Kpi, v: number) =>
  kpi === "minMTBF" ? `${num(v)} min` : kpi === "output" ? num(v) : `${num(v, 1)}%`;

function sentence(t: TFunction, i: Insight): string {
  const prefix = "machine" in i && i.machine ? `${i.machine.label}: ` : "";
  switch (i.kind) {
    case "outputReached":
      return t("liveInsights.outputReached", { output: num(i.output), target: num(i.target) });
    case "outputAhead":
    case "outputBehind":
      return t(`liveInsights.${i.kind}`, {
        gap: num(i.gap),
        projected: i.projected === null ? "--" : num(i.projected),
        target: num(i.target),
      });
    case "outputNeeded":
      return t("liveInsights.outputNeeded", {
        needed: num(i.neededPerMin, 1),
        avg: num(i.avgPerMin, 1),
        remaining: formatHm(i.remainingMs),
      });
    case "belowTarget":
    case "aboveMax":
      return (
        prefix +
        t(`liveInsights.${i.kind}`, {
          kpi: kpiLabel(t, i.kpi),
          value: kpiValue(i.kpi, i.value),
          target: kpiValue(i.kpi, i.target),
        })
      );
    case "downtimeBudget":
      return (
        prefix +
        t(i.usedSeconds > i.maxSeconds ? "liveInsights.downtimeExceeded" : "liveInsights.downtimeBudget", {
          used: formatHms(i.usedSeconds),
          max: formatHms(i.maxSeconds),
          over: formatHms(i.usedSeconds - i.maxSeconds),
        })
      );
    case "topStop":
      return (
        prefix +
        t("liveInsights.topStop", {
          name: i.name,
          duration: formatHms(i.seconds),
          count: i.count,
          share: `${num(i.sharePct)}%`,
        })
      );
    case "lineDownNow":
      return t("liveInsights.lineDownNow", {
        time: localTime(new Date(i.since).toISOString()),
        cause: i.cause.label,
        reason: i.reason ? ` (${i.reason})` : "",
      });
    case "topBlocker":
      return t("liveInsights.topBlocker", {
        cause: i.cause.label,
        duration: formatHms(i.seconds),
        share: `${num(i.sharePct)}%`,
        reason: i.reason ? `, ${i.reason}` : "",
        waiting: formatHms(i.waitingSeconds),
      });
  }
}

export const LiveInsights: React.FC<{ insights: Insight[]; className?: string }> = ({
  insights,
  className,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={cx(
        "rounded-2xl overflow-hidden",
        "bg-white dark:bg-slate-800",
        "border border-gray-100 dark:border-slate-700",
        "shadow-sm dark:shadow-lg dark:shadow-black/20",
        className,
      )}
    >
      <div className="px-5 pt-4 pb-2 flex items-center gap-2">
        <span className="text-base" aria-hidden="true">
          💡
        </span>
        <h4 className="font-bold text-sm text-gray-900 dark:text-slate-50">{t("liveInsights.title")}</h4>
      </div>
      {insights.length === 0 ? (
        <p className="px-5 pb-4 text-sm text-gray-500 dark:text-slate-400">{t("liveInsights.none")}</p>
      ) : (
        <ul className="px-5 pb-4 space-y-2">
          {insights.map((insight, idx) => (
            <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-slate-200">
              <span
                className={cx("mt-1.5 h-2 w-2 flex-shrink-0 rounded-full", DOT[insight.severity])}
                aria-hidden="true"
              />
              <span>{sentence(t, insight)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
